import React, { useState, useEffect, useCallback } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { RefreshCw, Download, Target, Calendar } from 'lucide-react';
import * as XLSX from 'xlsx';

export default function TargetBalanceSheetDashboard() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [selectedSeasons, setSelectedSeasons] = useState([]);
  const [selectedCommits, setSelectedCommits] = useState([]);
  const [selectedPrograms, setSelectedPrograms] = useState([]);

  // Default startDate to most recent Monday
  const getDefaultMonday = () => {
    const d = new Date();
    const day = d.getDay();
    const diff = day === 0 ? 6 : day - 1;
    d.setDate(d.getDate() - diff);
    return d.toISOString().slice(0, 10);
  };
  const [startDate, setStartDate] = useState(getDefaultMonday);

  const fmt = (v) => {
    const n = Number(v) || 0;
    return n === 0 ? '-' : n.toLocaleString('en-US');
  };

  // CHA-396 — commitment columns (Committed / Original / Rebuy / On PO / Not Produced / Remaining)
  // are UNKNOWN for commits the commitment feeds don't cover: the header, PO, OTB and assortment
  // exports carry only the current commit book, so prior-season commits arrive with no order data
  // at all. The backend now returns NULL there rather than a confident 0 — render that difference,
  // because '-' (a real zero) and "we don't know" must not look identical on a buying document.
  //   n/a  — every row in the group is inventory-only
  //   1,234* — partial: some rows in the group have no commitment data, so the sum is a floor
  const fmtCommit = (v, g) => {
    if (!g || !g.commitmentUnknown) return fmt(v);
    if (g.commitmentUnknown >= g.rowCount) return <span className="text-slate-400 italic" title="No commitment data for these commits — see CHA-398">n/a</span>;
    return <span title={`${g.commitmentUnknown} of ${g.rowCount} rows have no commitment data — this total is a floor`}>{fmt(v)}<span className="text-amber-600">*</span></span>;
  };

  // Same distinction for a single line-item row (CodeRabbit): a null on an inventory-only row must
  // not render as '-', which is what a genuine zero looks like.
  const fmtCommitRow = (v, row) =>
    row?.data_coverage === 'inventory_only'
      ? <span className="text-slate-400 italic" title="No commitment data for this commit — see CHA-398">n/a</span>
      : fmt(v);

  const fmtCurrency = (v) => {
    const n = Number(v) || 0;
    return n === 0 ? '-' : `$${n.toFixed(2)}`;
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const functions = getFunctions();
      const getTargetBalanceSheet = httpsCallable(functions, 'getTargetBalanceSheet', { timeout: 150000 });
      const result = await getTargetBalanceSheet({
        seasonFilter: selectedSeasons.length > 0 ? selectedSeasons : null,
        commitFilter: selectedCommits.length > 0 ? selectedCommits : null,
        programFilter: selectedPrograms.length > 0 ? selectedPrograms : null,
        startDate,
      });
      setData(result.data);
    } catch (err) {
      console.error('Error fetching Target balance sheet:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [selectedSeasons, selectedCommits, selectedPrograms, startDate]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const toggleSeason = (s) => {
    setSelectedSeasons(prev => prev.includes(s) ? prev.filter(x => x !== s) : [...prev, s]);
  };

  const toggleCommit = (c) => {
    setSelectedCommits(prev => prev.includes(c) ? prev.filter(x => x !== c) : [...prev, c]);
  };

  const toggleProgram = (p) => {
    setSelectedPrograms(prev => prev.includes(p) ? prev.filter(x => x !== p) : [...prev, p]);
  };

  const exportToExcel = () => {
    if (!filteredRows?.length) return;
    const bd = data.bucketDates || [];
    const ws = XLSX.utils.json_to_sheet(filteredRows.map(r => ({
      'Season': r.season_year,
      'Data coverage': r.data_coverage || '',
      'Commit #': r.commit_id,
      'Program': r.program,
      'Description': r.item_description,
      'Size': r.size,
      'Color': r.color || '',
      'GMS Style': r.target_style,
      'Vendor Style': r.vendor_style,
      'DPCI': r.dpci,
      'UPC': r.upc || '',
      'Type': r.item_type,
      'Purpose': r.purpose || '',
      'Committed': r.committed == null ? '' : Number(r.committed),
      'Original': r.original_commit == null ? '' : Number(r.original_commit),
      'Rebuy': r.rebuy == null ? '' : Number(r.rebuy),
      'On PO': r.on_po == null ? '' : Number(r.on_po),
      'Produced': Number(r.produced) || 0,
      'Received': Number(r.received) || 0,
      'Invoiced': Number(r.invoiced) || 0,
      'Open': Number(r.open_qty) || 0,
      'Pick': Number(r.pick) || 0,
      'QOH': Number(r.qoh) || 0,
      'Available': Number(r.available) || 0,
      'Asst ATS': Number(r.asst_ats) || 0,
      'WIP': Number(r.wip) || 0,
      [bd[0] || 'B1']: Number(r.wip_b1) || 0,
      [bd[1] || 'B2']: Number(r.wip_b2) || 0,
      [bd[2] || 'B3']: Number(r.wip_b3) || 0,
      [bd[3] || 'B4']: Number(r.wip_b4) || 0,
      [bd[4] || 'B5']: Number(r.wip_b5) || 0,
      'Future': Number(r.wip_future) || 0,
      'Planned AB': r.planned_ab_date || '',
      'ECOM Launch': r.ecom_launch_date || '',
      'Cost': Number(r.domestic_cost) || 0,
      'Retail': Number(r.retail) || 0,
      'Not Produced': r.not_produced == null ? '' : Number(r.not_produced),
      'Unaccounted': Number(r.unaccounted) || 0,
      'ER Qty': Number(r.er_qty) || 0,
    })));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Target Balance Sheet');
    XLSX.writeFile(wb, `Target_Balance_Sheet_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  const totals = data?.totals || {};
  const rows = data?.rows || [];
  const seasons = data?.filters?.seasons || [];
  const commits = data?.filters?.commits || [];
  const programs = data?.filters?.programs || [];
  const bucketDates = data?.bucketDates || [];

  // Local filters from summary table clicks (arrays for multi-select)
  const [filterSeasons, setFilterSeasons] = useState([]);
  const [filterBrands, setFilterBrands] = useState([]);
  const [filterCommits, setFilterCommits] = useState([]);

  const toggleFilter = (setter, value) => setter(prev => prev.includes(value) ? prev.filter(x => x !== value) : [...prev, value]);

  // Aggregate helper
  const aggregate = (items) => {
    const commits = new Set();
    let committed = 0, original = 0, rebuy = 0, produced = 0, invoiced = 0, qoh = 0, wip = 0, available = 0, not_produced = 0, unaccounted = 0, er_qty = 0;
    // CHA-396: count rows whose commitment columns are unknown (not zero) so the sums can be
    // labelled as floors rather than presented as complete.
    let commitmentUnknown = 0;
    items.forEach(r => {
      if (r.data_coverage === 'inventory_only') commitmentUnknown += 1;
      commits.add(r.commit_id);
      committed += Number(r.committed) || 0;
      original += Number(r.original_commit) || 0;
      rebuy += Number(r.rebuy) || 0;
      produced += Number(r.produced) || 0;
      invoiced += Number(r.invoiced) || 0;
      qoh += Number(r.qoh) || 0;
      wip += Number(r.wip) || 0;
      available += Number(r.available) || 0;
      not_produced += Number(r.not_produced) || 0;
      unaccounted += Number(r.unaccounted) || 0;
      er_qty += Number(r.er_qty) || 0;
    });
    return { commitCount: commits.size, committed, original, rebuy, produced, invoiced, qoh, wip, available, not_produced, unaccounted, er_qty, remaining: committed - invoiced, commitmentUnknown, rowCount: items.length };
  };

  // Sort season by year (last 4 digits) then by cycle number (C1, C2, C3, C4)
  const seasonSortKey = (s) => {
    const yearMatch = s.match(/(\d{4})/);
    const cycleMatch = s.match(/C(\d)/i);
    const year = yearMatch ? parseInt(yearMatch[1]) : 0;
    const cycle = cycleMatch ? parseInt(cycleMatch[1]) : 0;
    return year * 10 + cycle;
  };

  const componentRows = rows.filter(r => r.item_type === 'Component');

  // Helper: check if value passes array filter (empty array = no filter)
  const passFilter = (arr, val) => arr.length === 0 || arr.includes(val);

  // By Season (respects brand + commit filters)
  const seasonSummary = {};
  componentRows.filter(r => passFilter(filterBrands, r.program) && passFilter(filterCommits, r.commit_id)).forEach(r => {
    if (!seasonSummary[r.season_year]) seasonSummary[r.season_year] = [];
    seasonSummary[r.season_year].push(r);
  });
  const seasonRows = Object.entries(seasonSummary)
    .map(([season, items]) => ({ season, ...aggregate(items) }))
    .sort((a, b) => seasonSortKey(a.season) - seasonSortKey(b.season));

  // By Brand (respects season + commit filters)
  const brandSummary = {};
  componentRows.filter(r => passFilter(filterSeasons, r.season_year) && passFilter(filterCommits, r.commit_id)).forEach(r => {
    const brand = r.program || 'Unknown';
    if (!brandSummary[brand]) brandSummary[brand] = [];
    brandSummary[brand].push(r);
  });
  const brandRows = Object.entries(brandSummary)
    .map(([brand, items]) => ({ brand, ...aggregate(items) }))
    .sort((a, b) => a.brand.localeCompare(b.brand));

  // By Commit (respects season + brand filters)
  const commitSummary = {};
  componentRows.filter(r => passFilter(filterSeasons, r.season_year) && passFilter(filterBrands, r.program)).forEach(r => {
    const cid = r.commit_id || 'Unknown';
    if (!commitSummary[cid]) commitSummary[cid] = { commit: cid, season: r.season_year, brand: r.program || 'Unknown', items: [] };
    commitSummary[cid].items.push(r);
  });
  const commitRows = Object.values(commitSummary)
    .map(s => ({ commit: s.commit, season: s.season, brand: s.brand, ...aggregate(s.items) }))
    .sort((a, b) => seasonSortKey(a.season) - seasonSortKey(b.season) || a.brand.localeCompare(b.brand) || a.commit.localeCompare(b.commit));

  // Filtered detail rows
  const filteredRows = rows.filter(r =>
    passFilter(filterSeasons, r.season_year) &&
    passFilter(filterBrands, r.program) &&
    passFilter(filterCommits, r.commit_id)
  );

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="bg-white rounded-lg border border-slate-200 p-4 space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Target className="h-5 w-5 text-red-600" />
            <h2 className="font-semibold text-slate-800">Target Balance Sheet</h2>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={fetchData} disabled={loading}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-slate-600 hover:text-slate-800 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors">
              <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            <button onClick={exportToExcel} disabled={!filteredRows.length}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-emerald-700 bg-emerald-50 hover:bg-emerald-100 rounded-lg transition-colors disabled:opacity-40">
              <Download className="h-3.5 w-3.5" />
              Export XLSX
            </button>
          </div>
        </div>

        {/* Season Filter */}
        {seasons.length > 0 && (
          <div>
            <span className="text-xs font-medium text-slate-500 mr-2">Season:</span>
            <div className="inline-flex flex-wrap gap-1.5">
              {seasons.map(s => (
                <button key={s} onClick={() => toggleSeason(s)}
                  className={`px-2.5 py-1 text-xs rounded-full font-medium transition-colors ${
                    selectedSeasons.includes(s)
                      ? 'bg-red-600 text-white'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}>
                  {s}
                </button>
              ))}
              {selectedSeasons.length > 0 && (
                <button onClick={() => setSelectedSeasons([])}
                  className="px-2 py-1 text-xs text-slate-400 hover:text-slate-600">Clear</button>
              )}
            </div>
          </div>
        )}

        {/* Commit Filter */}
        {commits.length > 0 && (
          <div>
            <span className="text-xs font-medium text-slate-500 mr-2">Commit:</span>
            <div className="inline-flex flex-wrap gap-1.5">
              {commits.map(c => (
                <button key={c} onClick={() => toggleCommit(c)}
                  className={`px-2.5 py-1 text-xs rounded-full font-medium transition-colors ${
                    selectedCommits.includes(c)
                      ? 'bg-red-600 text-white'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}>
                  {c}
                </button>
              ))}
              {selectedCommits.length > 0 && (
                <button onClick={() => setSelectedCommits([])}
                  className="px-2 py-1 text-xs text-slate-400 hover:text-slate-600">Clear</button>
              )}
            </div>
          </div>
        )}

        {/* Program (Brand) Filter */}
        {programs.length > 0 && (
          <div>
            <span className="text-xs font-medium text-slate-500 mr-2">Brand:</span>
            <div className="inline-flex flex-wrap gap-1.5">
              {programs.map(p => (
                <button key={p} onClick={() => toggleProgram(p)}
                  className={`px-2.5 py-1 text-xs rounded-full font-medium transition-colors max-w-[200px] truncate ${
                    selectedPrograms.includes(p)
                      ? 'bg-red-600 text-white'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                  title={p}>
                  {p}
                </button>
              ))}
              {selectedPrograms.length > 0 && (
                <button onClick={() => setSelectedPrograms([])}
                  className="px-2 py-1 text-xs text-slate-400 hover:text-slate-600">Clear</button>
              )}
            </div>
          </div>
        )}

        {/* WIP Start Date */}
        <div className="flex items-center gap-2">
          <Calendar className="h-3.5 w-3.5 text-slate-400" />
          <span className="text-xs font-medium text-slate-500">WIP Start:</span>
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="px-2 py-1 text-xs border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-1 focus:ring-red-300"
          />
          {bucketDates.length > 0 && (
            <span className="text-[10px] text-slate-400 ml-1">
              Buckets: {bucketDates.join(' → ')} → Future
            </span>
          )}
        </div>
      </div>

      {/* CHA-396 — say plainly when part of the sheet has no commitment data, rather than letting
          zeros imply "nothing on order" for prior-season commits. */}
      {(() => {
        const io = rows.filter(r => r.data_coverage === 'inventory_only');
        if (!io.length) return null;
        const ioCommits = new Set(io.map(r => r.commit_id)).size;
        return (
          <div className="bg-white border border-slate-200 border-l-[3px] border-l-amber-500 px-4 py-2.5 text-xs text-slate-700">
            <strong>{ioCommits} commits are inventory-only.</strong> Target's commitment feeds (header / PO / OTB / assortment)
            cover only the current commit book, so for these — mostly prior-season cycles — Committed, Original, Rebuy, On PO
            and Not Produced are <span className="text-slate-400 italic">n/a</span> rather than 0, and totals that include them
            are marked <span className="text-amber-600">*</span> (a floor). Their inventory columns — QOH, Available, Produced,
            Invoiced, WIP — are complete and can be trusted.
          </div>
        );
      })()}

      {/* Summary Cards */}
      <div className="grid grid-cols-5 gap-3">
        {[
          { label: 'Committed', value: totals.committed, color: 'slate', commitment: true },
          { label: 'On PO', value: totals.on_po, color: 'blue', commitment: true },
          { label: 'Produced', value: totals.produced, color: 'violet' },
          { label: 'QOH', value: totals.qoh, color: 'emerald' },
          { label: 'WIP', value: totals.wip, color: 'amber' },
        ].map(card => (
          <div key={card.label} className={`bg-white rounded-lg border border-${card.color}-200 p-3`}>
            <div className={`text-xs font-medium text-${card.color}-500`}>{card.label}</div>
            <div className={`text-xl font-bold text-${card.color}-700 mt-1`}>
              {/* CHA-396: a commitment KPI that includes inventory-only rows is a floor, not a total */}
              {fmt(card.value)}
              {card.commitment && rows.some(r => r.data_coverage === 'inventory_only') && (
                <span className="text-amber-600 text-sm" title="Excludes commits with no commitment data — this is a floor">*</span>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Filter indicator */}
      {(filterSeasons.length > 0 || filterBrands.length > 0 || filterCommits.length > 0) && (
        <div className="flex items-center gap-2 text-xs flex-wrap">
          <span className="text-slate-500">Viewing:</span>
          {filterSeasons.map(s => <span key={s} className="px-2 py-1 bg-red-100 text-red-700 rounded-full font-medium cursor-pointer" onClick={() => toggleFilter(setFilterSeasons, s)}>{s} &times;</span>)}
          {filterBrands.map(b => <span key={b} className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full font-medium cursor-pointer" onClick={() => toggleFilter(setFilterBrands, b)}>{b} &times;</span>)}
          {filterCommits.map(c => <span key={c} className="px-2 py-1 bg-violet-100 text-violet-700 rounded-full font-medium cursor-pointer" onClick={() => toggleFilter(setFilterCommits, c)}>{c} &times;</span>)}
          <button onClick={() => { setFilterSeasons([]); setFilterBrands([]); setFilterCommits([]); }} className="px-2 py-1 text-slate-400 hover:text-slate-600">Clear All</button>
        </div>
      )}

      {/* Season + Brand summary tables side by side */}
      {!loading && (seasonRows.length > 0 || brandRows.length > 0) && (
        <div className="grid grid-cols-2 gap-4">
          {/* By Season */}
          <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
            <div className="px-4 py-2 bg-slate-50 border-b border-slate-200">
              <h3 className="text-xs font-semibold text-slate-600">By Season</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-slate-50">
                  <tr className="border-b border-slate-200">
                    {['Season', '#', 'Committed', 'Original', 'Rebuy', 'Produced', 'Invoiced', 'QOH', 'WIP', 'Not Produced', 'Unaccounted', 'ER Qty', 'Remaining'].map(h => (
                      <th key={h} className={`px-2 py-1.5 font-semibold text-slate-600 ${h === 'Season' ? 'text-left' : 'text-right'} whitespace-nowrap`}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {seasonRows.map((s, i) => (
                    <tr key={i} onClick={() => toggleFilter(setFilterSeasons, s.season)}
                      className={`border-b border-slate-100 cursor-pointer transition-colors ${filterSeasons.includes(s.season) ? 'bg-red-50 ring-1 ring-inset ring-red-200' : 'hover:bg-slate-50'}`}>
                      <td className="px-2 py-1.5 text-left font-medium">{s.season}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums text-slate-400">{s.commitCount}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums font-bold">{fmtCommit(s.committed, s)}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums">{fmtCommit(s.original, s)}</td>
                      <td className={`px-2 py-1.5 text-right tabular-nums ${s.rebuy > 0 ? 'text-blue-600' : ''}`}>{fmtCommit(s.rebuy, s)}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums">{fmt(s.produced)}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums">{fmt(s.invoiced)}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums">{fmt(s.qoh)}</td>
                      <td className={`px-2 py-1.5 text-right tabular-nums ${s.wip > 0 ? 'text-amber-600' : ''}`}>{fmt(s.wip)}</td>
                      <td className={`px-2 py-1.5 text-right tabular-nums bg-amber-50 ${s.not_produced > 0 ? 'text-orange-600' : ''}`}>{fmtCommit(s.not_produced, s)}</td>
                      <td className={`px-2 py-1.5 text-right tabular-nums bg-rose-50 ${s.unaccounted !== 0 ? 'text-red-600 font-medium' : ''}`}>{fmt(s.unaccounted)}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums">{fmt(s.er_qty)}</td>
                      <td className={`px-2 py-1.5 text-right tabular-nums font-medium ${s.remaining > 0 ? 'text-orange-600' : 'text-emerald-600'}`}>{fmtCommit(s.remaining, s)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* By Brand */}
          <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
            <div className="px-4 py-2 bg-slate-50 border-b border-slate-200">
              <h3 className="text-xs font-semibold text-slate-600">By Brand</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-slate-50">
                  <tr className="border-b border-slate-200">
                    {['Brand', '#', 'Committed', 'Original', 'Rebuy', 'Produced', 'Invoiced', 'QOH', 'WIP', 'Not Produced', 'Unaccounted', 'ER Qty', 'Remaining'].map(h => (
                      <th key={h} className={`px-2 py-1.5 font-semibold text-slate-600 ${h === 'Brand' ? 'text-left' : 'text-right'} whitespace-nowrap`}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {brandRows.map((s, i) => (
                    <tr key={i} onClick={() => toggleFilter(setFilterBrands, s.brand)}
                      className={`border-b border-slate-100 cursor-pointer transition-colors ${filterBrands.includes(s.brand) ? 'bg-blue-50 ring-1 ring-inset ring-blue-200' : 'hover:bg-slate-50'}`}>
                      <td className="px-2 py-1.5 text-left font-medium max-w-[140px] truncate" title={s.brand}>{s.brand}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums text-slate-400">{s.commitCount}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums font-bold">{fmtCommit(s.committed, s)}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums">{fmtCommit(s.original, s)}</td>
                      <td className={`px-2 py-1.5 text-right tabular-nums ${s.rebuy > 0 ? 'text-blue-600' : ''}`}>{fmtCommit(s.rebuy, s)}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums">{fmt(s.produced)}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums">{fmt(s.invoiced)}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums">{fmt(s.qoh)}</td>
                      <td className={`px-2 py-1.5 text-right tabular-nums ${s.wip > 0 ? 'text-amber-600' : ''}`}>{fmt(s.wip)}</td>
                      <td className={`px-2 py-1.5 text-right tabular-nums bg-amber-50 ${s.not_produced > 0 ? 'text-orange-600' : ''}`}>{fmtCommit(s.not_produced, s)}</td>
                      <td className={`px-2 py-1.5 text-right tabular-nums bg-rose-50 ${s.unaccounted !== 0 ? 'text-red-600 font-medium' : ''}`}>{fmt(s.unaccounted)}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums">{fmt(s.er_qty)}</td>
                      <td className={`px-2 py-1.5 text-right tabular-nums font-medium ${s.remaining > 0 ? 'text-orange-600' : 'text-emerald-600'}`}>{fmtCommit(s.remaining, s)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* By Commit */}
      {!loading && commitRows.length > 0 && (
        <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
          <div className="px-4 py-2 bg-slate-50 border-b border-slate-200">
            <h3 className="text-xs font-semibold text-slate-600">By Commit</h3>
          </div>
          <div className="overflow-x-auto max-h-[300px] overflow-y-auto">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 sticky top-0">
                <tr className="border-b border-slate-200">
                  {['Commit #', 'Season', 'Brand', 'Committed', 'Original', 'Rebuy', 'Produced', 'Invoiced', 'QOH', 'Available', 'WIP', 'Not Produced', 'Unaccounted', 'ER Qty', 'Remaining'].map(h => (
                    <th key={h} className={`px-2 py-1.5 font-semibold text-slate-600 ${['Commit #', 'Season', 'Brand'].includes(h) ? 'text-left' : 'text-right'} whitespace-nowrap`}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {commitRows.map((s, i) => (
                  <tr key={i} onClick={() => toggleFilter(setFilterCommits, s.commit)}
                    className={`border-b border-slate-100 cursor-pointer transition-colors ${filterCommits.includes(s.commit) ? 'bg-violet-50 ring-1 ring-inset ring-violet-200' : 'hover:bg-slate-50'}`}>
                    <td className="px-2 py-1.5 text-left font-medium">{s.commit}</td>
                    <td className="px-2 py-1.5 text-left">{s.season}</td>
                    <td className="px-2 py-1.5 text-left max-w-[140px] truncate" title={s.brand}>{s.brand}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums font-bold">{fmtCommit(s.committed, s)}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums">{fmtCommit(s.original, s)}</td>
                    <td className={`px-2 py-1.5 text-right tabular-nums ${s.rebuy > 0 ? 'text-blue-600' : ''}`}>{fmtCommit(s.rebuy, s)}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums">{fmt(s.produced)}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums">{fmt(s.invoiced)}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums">{fmt(s.qoh)}</td>
                    <td className={`px-2 py-1.5 text-right tabular-nums ${s.available > 0 ? 'text-emerald-600' : ''}`}>{fmt(s.available)}</td>
                    <td className={`px-2 py-1.5 text-right tabular-nums ${s.wip > 0 ? 'text-amber-600' : ''}`}>{fmt(s.wip)}</td>
                    <td className={`px-2 py-1.5 text-right tabular-nums bg-amber-50 ${s.not_produced > 0 ? 'text-orange-600' : ''}`}>{fmtCommit(s.not_produced, s)}</td>
                    <td className={`px-2 py-1.5 text-right tabular-nums bg-rose-50 ${s.unaccounted !== 0 ? 'text-red-600 font-medium' : ''}`}>{fmt(s.unaccounted)}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums">{fmt(s.er_qty)}</td>
                    <td className={`px-2 py-1.5 text-right tabular-nums font-medium ${s.remaining > 0 ? 'text-orange-600' : 'text-emerald-600'}`}>{fmtCommit(s.remaining, s)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Loading / Error */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="h-6 w-6 text-red-500 animate-spin" />
          <span className="ml-2 text-slate-500">Loading balance sheet...</span>
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-700">
          <strong>Error:</strong> {error}
        </div>
      )}

      {/* Data Table */}
      {!loading && !error && filteredRows.length > 0 && (
        <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 sticky top-0 z-10">
                <tr className="border-b border-slate-200">
                  {[
                    { label: 'Season', align: 'left' },
                    { label: 'Commit #', align: 'left' },
                    { label: 'Brand', align: 'left' },
                    { label: 'Description', align: 'left' },
                    { label: 'Size', align: 'center' },
                    { label: 'Color', align: 'left' },
                    { label: 'GMS Style', align: 'left' },
                    { label: 'Vendor Style', align: 'left' },
                    { label: 'DPCI', align: 'left' },
                    { label: 'UPC', align: 'left' },
                    { label: 'Type', align: 'center' },
                    { label: 'Purpose', align: 'left' },
                    { label: 'Committed', align: 'right' },
                    { label: 'Original', align: 'right' },
                    { label: 'Rebuy', align: 'right' },
                    { label: 'On PO', align: 'right' },
                    { label: 'Produced', align: 'right' },
                    { label: 'Received', align: 'right' },
                    { label: 'Invoiced', align: 'right' },
                    { label: 'Open', align: 'right' },
                    { label: 'Pick', align: 'right' },
                    { label: 'QOH', align: 'right' },
                    { label: 'Available', align: 'right' },
                    { label: 'Asst ATS', align: 'right' },
                    { label: 'WIP', align: 'right' },
                    ...(bucketDates.map(d => ({ label: d, align: 'right' }))),
                    { label: 'Future', align: 'right' },
                    { label: 'Planned AB', align: 'center' },
                    { label: 'ECOM Launch', align: 'center' },
                    { label: 'Cost', align: 'right' },
                    { label: 'Retail', align: 'right' },
                    { label: 'Not Produced', align: 'right' },
                    { label: 'Unaccounted', align: 'right' },
                    { label: 'ER Qty', align: 'right' },
                  ].map(h => (
                    <th key={h.label} className={`px-2 py-2 font-semibold text-slate-600 text-${h.align} whitespace-nowrap`}>
                      {h.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredRows.map((r, i) => {
                  const isAssortment = r.item_type === 'Assortment Box';
                  const available = Number(r.available) || 0;
                  const wip = Number(r.wip) || 0;
                  return (
                    <tr key={i} className={`border-b border-slate-100 hover:bg-slate-50 ${isAssortment ? 'bg-indigo-50/50' : ''}`}>
                      <td className="px-2 py-1.5 text-left whitespace-nowrap">{r.season_year || '-'}</td>
                      <td className="px-2 py-1.5 text-left whitespace-nowrap">{r.commit_id || '-'}</td>
                      <td className="px-2 py-1.5 text-left max-w-[140px] truncate" title={r.program}>{r.program || '-'}</td>
                      <td className="px-2 py-1.5 text-left max-w-[180px] truncate" title={r.item_description}>{r.item_description || '-'}</td>
                      <td className="px-2 py-1.5 text-center">{r.size || '-'}</td>
                      <td className="px-2 py-1.5 text-left whitespace-nowrap">{r.color || '-'}</td>
                      <td className="px-2 py-1.5 text-left whitespace-nowrap">{r.target_style || '-'}</td>
                      <td className="px-2 py-1.5 text-left whitespace-nowrap">{r.vendor_style || '-'}</td>
                      <td className="px-2 py-1.5 text-left whitespace-nowrap">{r.dpci || '-'}</td>
                      <td className="px-2 py-1.5 text-left whitespace-nowrap font-mono text-[10px]">{r.upc || '-'}</td>
                      <td className="px-2 py-1.5 text-center">
                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                          isAssortment ? 'bg-indigo-100 text-indigo-700' : 'bg-slate-100 text-slate-600'
                        }`}>
                          {r.item_type || '-'}
                        </span>
                      </td>
                      <td className="px-2 py-1.5 text-left max-w-[160px] truncate text-[10px] text-slate-500" title={r.purpose}>{r.purpose || '-'}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums font-bold">{fmtCommitRow(r.committed, r)}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums">{fmtCommitRow(r.original_commit, r)}</td>
                      <td className={`px-2 py-1.5 text-right tabular-nums ${Number(r.rebuy) > 0 ? 'text-blue-600 font-medium' : ''}`}>{fmtCommitRow(r.rebuy, r)}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums">{fmtCommitRow(r.on_po, r)}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums">{fmt(r.produced)}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums">{fmt(r.received)}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums">{fmt(r.invoiced)}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums">{fmt(r.open_qty)}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums">{fmt(r.pick)}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums">{fmt(r.qoh)}</td>
                      <td className={`px-2 py-1.5 text-right tabular-nums ${available > 0 ? 'text-emerald-600 font-medium' : available < 0 ? 'text-red-500' : ''}`}>
                        {fmt(r.available)}
                      </td>
                      <td className={`px-2 py-1.5 text-right tabular-nums ${Number(r.asst_ats) > 0 ? 'text-indigo-600 font-medium' : ''}`}>
                        {fmt(r.asst_ats)}
                      </td>
                      <td className={`px-2 py-1.5 text-right tabular-nums ${wip > 0 ? 'text-amber-600 font-medium' : ''}`}>
                        {fmt(r.wip)}
                      </td>
                      {['wip_b1', 'wip_b2', 'wip_b3', 'wip_b4', 'wip_b5', 'wip_future'].map(bk => (
                        <td key={bk} className={`px-2 py-1.5 text-right tabular-nums ${Number(r[bk]) > 0 ? 'text-amber-600 font-medium' : ''}`}>
                          {fmt(r[bk])}
                        </td>
                      ))}
                      <td className="px-2 py-1.5 text-center whitespace-nowrap text-[10px]">
                        {r.planned_ab_date || '-'}
                      </td>
                      <td className="px-2 py-1.5 text-center whitespace-nowrap text-[10px]">
                        {r.ecom_launch_date || '-'}
                      </td>
                      <td className="px-2 py-1.5 text-right tabular-nums">{fmtCurrency(r.domestic_cost)}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums">{fmtCurrency(r.retail)}</td>
                      <td className={`px-2 py-1.5 text-right tabular-nums bg-amber-50 ${Number(r.not_produced) > 0 ? 'text-orange-600 font-medium' : Number(r.not_produced) < 0 ? 'text-red-500' : ''}`}>{fmtCommitRow(r.not_produced, r)}</td>
                      <td className={`px-2 py-1.5 text-right tabular-nums bg-rose-50 ${Number(r.unaccounted) !== 0 ? 'text-red-600 font-medium' : ''}`}>{fmt(r.unaccounted)}</td>
                      <td className={`px-2 py-1.5 text-right tabular-nums bg-sky-50 ${Number(r.er_qty) > 0 ? 'text-sky-600 font-medium' : ''}`}>{fmt(r.er_qty)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="px-3 py-2 bg-slate-50 border-t border-slate-200 text-xs text-slate-500">
            {filteredRows.length.toLocaleString()} items{(filterSeasons.length > 0 || filterBrands.length > 0 || filterCommits.length > 0) ? ` (filtered from ${rows.length.toLocaleString()})` : ''}
          </div>
        </div>
      )}

      {!loading && !error && filteredRows.length === 0 && filterSeasons.length === 0 && filterBrands.length === 0 && filterCommits.length === 0 && (
        <div className="text-center py-12 text-slate-400 text-sm">
          No Target balance sheet data found. Upload commitment files first.
        </div>
      )}
    </div>
  );
}
